import { demoOverview } from "./data/demo";
import type {
  AuditLogEntry,
  AlertSla,
  AuthUser,
  ComponentBinding,
  ComponentBindingCreateInput,
  ComponentLayout,
  Device,
  DeviceCommissioning,
  DeviceCreateInput,
  DriverCompliance,
  FleetAlert,
  Geofence,
  DiagnosticCode,
  EvidenceClip,
  FuelEvent,
  FuelTankState,
  GeofenceEventDetail,
  GeofenceInput,
  IncidentCase,
  IntegrationProfile,
  InventoryOverview,
  MaintenanceForecast,
  MaintenanceOrder,
  Overview,
  PermissionMatrixRow,
  PositionSample,
  ProcurementPlan,
  ReeferCommand,
  ReeferUnit,
  RiskSnapshot,
  RouteCorridor,
  SecurityEvent,
  SensorAnomaly,
  SupplierQuote,
  TireState,
  Trailer,
  Trip,
  UserCreateInput,
  VehicleReadiness,
  VideoSession,
  WorkshopOrder,
} from "./types";

export const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
const TOKEN_KEY = "arconte_c5_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token: string) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export async function login(email: string, password: string) {
  const response = await fetch(`${API_URL}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) throw new Error("Credenciales incorrectas");
  const data = (await response.json()) as {
    access_token: string;
    user: AuthUser;
  };
  setToken(data.access_token);
  return data;
}

export async function getCurrentUser(): Promise<AuthUser> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("La sesión venció");
  return response.json();
}

export async function listUsers(): Promise<AuthUser[]> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/admin/users`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar los usuarios");
  return response.json();
}

export async function createUser(payload: UserCreateInput): Promise<AuthUser> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/admin/users`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible crear el usuario");
  }
  return response.json();
}

export async function listPermissionMatrix(): Promise<PermissionMatrixRow[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/admin/permissions`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar la matriz de permisos");
  return response.json();
}

export async function updateRolePermission(
  role: string,
  permissionCode: string,
  allowed: boolean,
): Promise<PermissionMatrixRow> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/admin/permissions`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      role,
      permission_code: permissionCode,
      allowed,
    }),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible actualizar el permiso");
  }
  return response.json();
}

export async function listAuditLogs(): Promise<AuditLogEntry[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/admin/audit?limit=500`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar la auditoría");
  return response.json();
}

export async function listDevices(): Promise<Device[]> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/devices`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar los dispositivos");
  return response.json();
}

export async function createDevice(payload: DeviceCreateInput): Promise<Device> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/devices`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible registrar el dispositivo");
  }
  return response.json();
}

export async function listDeviceBindings(deviceId: number): Promise<ComponentBinding[]> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/devices/${deviceId}/bindings`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar los bindings");
  return response.json();
}

export async function createDeviceBinding(
  deviceId: number,
  payload: ComponentBindingCreateInput,
): Promise<ComponentBinding> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/devices/${deviceId}/bindings`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible crear el binding");
  }
  return response.json();
}

export async function listIntegrationProfiles(): Promise<IntegrationProfile[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/integration-profiles`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar los perfiles técnicos");
  return response.json();
}

export async function listDeviceCommissioning(): Promise<DeviceCommissioning[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/device-commissioning`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar la certificación");
  return response.json();
}

export async function updateDeviceCommissioning(
  deviceId: number,
  payload: {
    profile_id?: number;
    stage: DeviceCommissioning["stage"];
    checklist_json: Record<string, boolean>;
    notes?: string;
  },
): Promise<DeviceCommissioning> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/devices/${deviceId}/commissioning`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible actualizar la certificación");
  }
  return response.json();
}

export async function getCurrentProcurementPlan(): Promise<ProcurementPlan> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/procurement/current`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar el plan de compras");
  return response.json();
}

export async function createSupplierQuote(payload: {
  plan_id: number;
  vendor_name: string;
  reference: string;
  currency: string;
  status: SupplierQuote["status"];
  valid_until?: string;
  lead_time_days: number;
  warranty_months: number;
  notes: string;
  lines: {
    procurement_item_id: number;
    unit_price: number;
    installation_unit_price: number;
    monthly_unit_price: number;
    tax_percent: number;
    included_json: string[];
    notes: string;
  }[];
}): Promise<SupplierQuote> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/procurement/quotes`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible registrar la propuesta");
  }
  return response.json();
}

export async function getOverview(): Promise<Overview> {
  const token = getToken();
  if (!token) return demoOverview;
  try {
    const response = await fetch(`${API_URL}/api/fleet/overview`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error("No se pudo cargar la operación");
    return response.json();
  } catch {
    return demoOverview;
  }
}

export async function getGeofences(): Promise<Geofence[]> {
  const token = getToken();
  if (!token) return [];
  try {
    const response = await fetch(`${API_URL}/api/geofences`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error("No se pudieron cargar las geocercas");
    return response.json();
  } catch {
    return [];
  }
}

export async function listAllGeofences(): Promise<Geofence[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/geofences?include_inactive=true`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar las geocercas");
  return response.json();
}

export async function listGeofenceEvents(): Promise<GeofenceEventDetail[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/geofences/events?limit=150`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar eventos de geocerca");
  return response.json();
}

export async function createGeofence(payload: GeofenceInput): Promise<Geofence> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/geofences`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible crear la geocerca");
  }
  return response.json();
}

export async function updateGeofence(
  geofenceId: number,
  payload: Partial<Omit<GeofenceInput, "code">>,
): Promise<Geofence> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/geofences/${geofenceId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible actualizar la geocerca");
  }
  return response.json();
}

export async function getRoutes(): Promise<RouteCorridor[]> {
  const token = getToken();
  if (!token) return [];
  try {
    const response = await fetch(`${API_URL}/api/routes`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error("No se pudieron cargar las rutas");
    return response.json();
  } catch {
    return [];
  }
}

export async function listDiagnostics(): Promise<DiagnosticCode[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/diagnostics`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No se pudieron consultar los diagnósticos");
  return response.json();
}

export async function listMaintenanceOrders(): Promise<MaintenanceOrder[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/maintenance/orders`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No se pudieron consultar las órdenes");
  return response.json();
}

export async function listWorkshopOrders(): Promise<WorkshopOrder[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/workshop/orders`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No se pudieron consultar las órdenes de taller");
  return response.json();
}

export async function updateWorkshopOrder(
  maintenanceOrderId: number,
  payload: {
    stage?: WorkshopOrder["stage"];
    technician_name?: string;
    workshop_name?: string;
    notes?: string;
    actual_cost?: number;
    task_id?: number;
    task_status?: "pending" | "in_progress" | "completed" | "skipped";
    event_note: string;
  },
): Promise<WorkshopOrder> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/workshop/orders/${maintenanceOrderId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible actualizar la orden de taller");
  }
  return response.json();
}

export async function getInventoryOverview(): Promise<InventoryOverview> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/inventory/overview`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar el almacén");
  return response.json();
}

export async function inventoryAction(payload: {
  action: "receive" | "reserve" | "issue" | "release";
  part_id: number;
  location_id: number;
  quantity: number;
  unit_cost?: number;
  maintenance_part_usage_id?: number;
  reference?: string;
  note: string;
}): Promise<InventoryOverview> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/inventory/actions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible registrar el movimiento");
  }
  return response.json();
}

export async function getComponentLayout(vehicleType: string): Promise<ComponentLayout | null> {
  const token = getToken();
  if (!token) return null;
  try {
    const response = await fetch(`${API_URL}/api/component-layouts/${encodeURIComponent(vehicleType)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error("No se pudo cargar el layout 3D");
    return response.json();
  } catch {
    return null;
  }
}

export async function acknowledgeAlert(alertId: number): Promise<FleetAlert> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/alerts/${alertId}/acknowledge`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No se pudo reconocer la alerta");
  return response.json();
}

export async function getVehicleTrail(vehicleId: number): Promise<PositionSample[]> {
  const token = getToken();
  if (!token) return [];
  try {
    const response = await fetch(`${API_URL}/api/vehicles/${vehicleId}/positions?limit=180`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error("No se pudo cargar el recorrido");
    return response.json();
  } catch {
    return [];
  }
}

export async function startVideoSession(cameraId: number): Promise<VideoSession> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/cameras/${cameraId}/sessions`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible abrir la transmisión");
  }
  return response.json();
}

export async function closeVideoSession(sessionId: number): Promise<void> {
  const token = getToken();
  if (!token) return;
  await fetch(`${API_URL}/api/video/sessions/${sessionId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function listEvidenceClips(): Promise<EvidenceClip[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/evidence/clips`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar la evidencia");
  return response.json();
}

export async function listVehicleTires(vehicleId: number): Promise<TireState[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/vehicles/${vehicleId}/tires`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar el TPMS");
  return response.json();
}

export async function getVehicleFuel(vehicleId: number): Promise<FuelTankState[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/vehicles/${vehicleId}/fuel`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar combustible");
  return response.json();
}

export async function listFuelEvents(vehicleId?: number): Promise<FuelEvent[]> {
  const token = getToken();
  if (!token) return [];
  const query = vehicleId ? `?vehicle_id=${vehicleId}` : "";
  const response = await fetch(`${API_URL}/api/fuel/events${query}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar eventos de combustible");
  return response.json();
}

export async function listSecurityEvents(): Promise<SecurityEvent[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/security/events`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar seguridad");
  return response.json();
}

export async function listTrailers(): Promise<Trailer[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/trailers`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar los remolques");
  return response.json();
}

export async function listReefers(): Promise<ReeferUnit[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/reefers`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar los reefers");
  return response.json();
}

export async function listReeferCommands(): Promise<ReeferCommand[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/reefer/commands`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar comandos reefer");
  return response.json();
}

export async function requestReeferSetpoint(
  reeferId: number,
  setpointC: number,
  reason: string,
): Promise<ReeferCommand> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/reefer/commands`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      reefer_id: reeferId,
      command_type: "set_setpoint",
      payload: { setpoint_c: setpointC },
      reason,
    }),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible solicitar el cambio");
  }
  return response.json();
}

export async function approveReeferCommand(commandId: number): Promise<ReeferCommand> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/reefer/commands/${commandId}/approve`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible aprobar el comando");
  }
  return response.json();
}

export async function listRiskSnapshots(): Promise<RiskSnapshot[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/intelligence/risk`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible calcular riesgo");
  return response.json();
}

export async function listAlertSla(): Promise<AlertSla[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/intelligence/alert-sla`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar los SLA de incidentes");
  return response.json();
}

export async function listSensorAnomalies(): Promise<SensorAnomaly[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/intelligence/anomalies`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar las anomalías");
  return response.json();
}

export async function listIncidents(): Promise<IncidentCase[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/incidents`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar los expedientes");
  return response.json();
}

export async function updateIncident(
  incidentId: number,
  payload: {
    status?: IncidentCase["status"];
    assign_to_me?: boolean;
    note?: string;
    resolution?: string;
  },
): Promise<IncidentCase> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/incidents/${incidentId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible actualizar el expediente");
  }
  return response.json();
}

export async function listTrips(): Promise<Trip[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/trips`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar los viajes");
  return response.json();
}

export async function updateTrip(
  tripId: number,
  status: Trip["status"],
  note?: string,
): Promise<Trip> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/trips/${tripId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ status, note }),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible actualizar el viaje");
  }
  return response.json();
}

export async function listVehicleReadiness(): Promise<VehicleReadiness[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/vehicles/readiness`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar la liberación de unidades");
  return response.json();
}

export async function createVehicleInspection(
  vehicleId: number,
  payload: {
    trip_id?: number;
    odometer_km?: number;
    notes: string;
    items: {
      item_code: string;
      label: string;
      result: "pass" | "fail" | "na";
      component_code?: string;
      notes?: string;
    }[];
  },
): Promise<VehicleReadiness> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/vehicles/${vehicleId}/inspections`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible registrar la inspección");
  }
  return response.json();
}

export async function listDriverCompliance(): Promise<DriverCompliance[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/drivers/compliance`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar los conductores");
  return response.json();
}

export async function driverShiftAction(
  driverId: number,
  action: "start_rest" | "resume" | "end_shift",
  note?: string,
): Promise<DriverCompliance> {
  const token = getToken();
  if (!token) throw new Error("Sesión no disponible");
  const response = await fetch(`${API_URL}/api/drivers/${driverId}/actions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ action, note }),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || "No fue posible actualizar el turno");
  }
  return response.json();
}

export async function listMaintenanceForecasts(): Promise<MaintenanceForecast[]> {
  const token = getToken();
  if (!token) return [];
  const response = await fetch(`${API_URL}/api/maintenance/forecasts`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("No fue posible consultar el mantenimiento predictivo");
  return response.json();
}

export function connectFleetSocket(onMessage: (event: MessageEvent) => void) {
  const token = getToken();
  if (!token) return () => undefined;
  const wsUrl = API_URL.replace(/^http/, "ws");
  const socket = new WebSocket(`${wsUrl}/ws/fleet?token=${encodeURIComponent(token)}`);
  socket.addEventListener("message", onMessage);
  const heartbeat = window.setInterval(() => {
    if (socket.readyState === WebSocket.OPEN) socket.send("ping");
  }, 25_000);
  return () => {
    window.clearInterval(heartbeat);
    socket.close();
  };
}
